/**
 * Combined Drawers - single custom drawer hosting both postal code and delivery-times views
 *
 * This controller mirrors the patterns used in assets/postal-code-drawer.js:
 * - Custom element registration with open()/close() methods
 * - Public API exposed via window.DeliveryCore
 * - Defensive null checks and minimal DOM work on open/close
 */

const COMBINED_CONSOLE_PREFIX = '[COMBINED-DRAWER]';

/**
 * Log helper (same style as postal-code-drawer.js)
 * @param {'log'|'warn'|'error'} level
 * @param {string} message
 * @param {...any} args
 */
function logMessage(level, message, ...args) {
  /** @type {any} */ (console)[level](`🔍 ${COMBINED_CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Safely get a single element
 * @param {Element} root
 * @param {string} selector
 * @returns {HTMLElement|null}
 */
function qs(root, selector) {
  const el = root.querySelector(selector);
  return el instanceof HTMLElement ? el : null;
}

/**
 * Combined drawer custom element
 */
class CombinedDrawerComponent extends HTMLElement {
  connectedCallback() {
    logMessage('log', 'CombinedDrawerComponent connected');
  }

  // ===== PRIVATE: DOM REFS =====

  /** @returns {HTMLElement|null} */ get postalView() {
    return qs(this, '#postalcode-drawer');
  }
  /** @returns {HTMLElement|null} */ get deliveryView() {
    return qs(this, '#delivery-times-drawer');
  }

  /**
   * Show requested view and open the drawer
   * @param {'postalcode'|'delivery'} view
   */
  openView(view) {
    try {
      logMessage('log', `Opening combined drawer with view: ${view}`);

      // Toggle views
      const postal = this.postalView;
      const delivery = this.deliveryView;
      if (postal && delivery) {
        postal.style.display = view === 'postalcode' ? '' : 'none';
        delivery.style.display = view === 'delivery' ? '' : 'none';
      } else {
        logMessage('warn', 'View containers missing in combined drawer');
      }

      // Open drawer
      // Allow CSS to apply before flagging open
      requestAnimationFrame(() => {
        this.setAttribute('open', '');
        this.setAttribute('aria-hidden', 'false');
      });

      document.querySelector('html')?.setAttribute("scroll-lock", "");

      // If postal view, optionally trigger auto submission check
      if (view === 'postalcode') {
        try {
          const el = /** @type {any} */ (document.querySelector('postcode-drawer-component'));
          el?.checkAndAutoSubmitPostalCode?.();
        } catch (_) {}
      }
    } catch (error) {
      logMessage('error', 'Error opening combined drawer', error);
    }
  }

  close() {
    try {
      logMessage('log', 'Closing combined drawer');

      this.removeAttribute('open');
      this.setAttribute('aria-hidden', 'true');
      document.querySelector('html')?.removeAttribute("scroll-lock");
    } catch (error) {
      logMessage('error', 'Error closing combined drawer', error);
    }
  }

  /**
   * Lazy-safe open; if element not yet connected, try again on next frame
   * @param {'postalcode'|'delivery'} view
   */
  safeOpen(view) {
    if (!document.body.contains(this)) {
      requestAnimationFrame(() => this.safeOpen(view));
      return true;
    }
    this.openView(view);
    return true;
  }
}

// Register custom element
if (!customElements.get('custom-drawer-component')) {
  customElements.define('custom-drawer-component', CombinedDrawerComponent);
}

// Global accessor (optional)
if (typeof window !== 'undefined') {
  /** @type {any} */ (window).CombinedDrawers = {
    openPostalCode: () => {
      const el = document.querySelector('custom-drawer-component[data-combined-drawers]');
      if (el instanceof CombinedDrawerComponent) return el.safeOpen('postalcode');
      return false;
    },
    openDelivery: () => {
      const el = document.querySelector('custom-drawer-component[data-combined-drawers]');
      if (el instanceof CombinedDrawerComponent) return el.safeOpen('delivery');
      return false;
    },
    close: () => {
      const el = document.querySelector('custom-drawer-component[data-combined-drawers]');
      if (el instanceof CombinedDrawerComponent) el.close();
    }
  };
}
